import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
} from "@material-ui/core";
import moment from "moment";
import { Card, Image, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Navbar from "../Navbar";
import { useParams, useHistory } from "react-router-dom";
import { createUserSocket } from "../../utils/userSocket";
import { fetchOrderDetails, updateOrder } from "../../redux/myOrdersRedux";
import styled from "styled-components";
import { mobile } from "../../responsive.js";
import OrderProgressStepper from "./OrderProgressStepper";
import MerchantPaymentReport from "./MerchantPaymentReport";
import DeliveryAdjustments from "./DeliveryAdjustments";
import {
  LocalShipping,
  Receipt,
  CalendarToday,
  Phone,
  LocationOn,
  Payment,
  ShoppingCart,
  ArrowBack,
  Description,
  FitnessCenter,
  WarningRounded,
  NoteOutlined,
  MyLocation,
  FlagOutlined,
  Close,
  Storefront,
} from "@material-ui/icons";
import { formatCurrency } from "../../config/market";
import { buildOrderSummary } from "../../utils/orderSummary";
import { deliveryLabel } from "../../utils/orderDisplay";
import { describeOrderDeliveryTime } from "../../utils/deliverySchedule";
import { userRequest } from "../../api/requestMethods";
import { toast } from "react-toastify";
import { describeDropoffPrecision, DROPOFF_TONES } from "../../utils/dropoffPrecision";
import ReportIssueButton from "../SupportCases/ReportIssueButton";

const BRAUN = {
  bg: "#f3f3f2",
  bgAlt: "#ececea",
  surface: "#ffffff",
  text: "#1f1f1f",
  textMuted: "#6e6e6e",
  border: "#d8d8d5",
  accent: "#2f2f2f",
  accentSoft: "#e9e9e6",
  warm: "#b08d57",
  success: "#2f7d32",
  danger: "#b42318",
};

const StyledContainer = styled.div`
  max-width: 1240px;
  margin-top: 120px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 24px 24px;
  background: linear-gradient(180deg, ${BRAUN.bg} 0%, ${BRAUN.bgAlt} 100%);
  border-radius: 16px;
  ${mobile({
  padding: "0 12px 18px",
  marginTop: "100px",
  borderRadius: "10px",
})}
`;

const PageContent = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
`;

const DesktopStepperWrapper = styled.div`
  display: block;

  ${mobile({
    display: "none",
  })}
`;

const MobileProgressCard = styled.div`
  display: none;

  ${mobile({
    display: "block",
    background: "linear-gradient(155deg, #2b2b2b 0%, #1f1f1f 100%)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "14px",
    padding: "14px",
    marginBottom: "18px",
    boxShadow: "0 12px 30px rgba(0, 0, 0, 0.24)",
  })}
`;

const MobileProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

const MobileProgressMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const MobileMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const MobileMetaLabel = styled.span`
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(255, 255, 255, 0.68);
  margin-bottom: 4px;
  font-weight: 600;
`;

const MobileMetaValue = styled.span`
  display: block;
  font-size: 0.85rem;
  color: #ffffff;
  font-weight: 700;
`;

const Wrapper = styled.div`
  margin: 0 auto;
  padding: 6px 0;
  width: 100%;
  ${mobile({
  padding: "8px 0",
})}
`;

const StyledPaper = styled(Paper)`
  padding: 22px;
  border-radius: 14px;
  background: ${BRAUN.surface};
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.06);
  border: 1px solid ${BRAUN.border};
  width: 100%;
  ${mobile({
  padding: "14px",
  borderRadius: "12px",
})}
`;

const HeaderSection = styled.div`
  background: linear-gradient(140deg, #363636 0%, #202020 100%);
  padding: 16px 18px;
  border-radius: 12px;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
  
  ${mobile({
  flexDirection: "column",
  alignItems: "flex-start",
})}
`;

const StatusSummary = styled.div`
  display: flex;
  gap: 10px;
  
  ${mobile({
  marginTop: "12px",
  flexWrap: "wrap",
})}
`;

const StatusBadge = styled.div`
  background: rgba(255, 255, 255, 0.09);
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 84px;
`;

const StatusLabel = styled.div`
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.72);
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 4px;
  letter-spacing: 0.6px;
`;

const StatusValue = styled.div`
  font-size: 0.95rem;
  color: #ffffff;
  font-weight: 700;
`;

const InfoCard = styled.div`
  background: ${BRAUN.surface};
  padding: 14px;
  border-radius: 10px;
  margin-bottom: 0;
  border: 1px solid ${BRAUN.border};
  border-top: 3px solid ${props => props.borderColor || BRAUN.warm};
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  }

  ${mobile({
    padding: "12px",
  })}
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;

  ${mobile({
    alignItems: "flex-start",
  })}
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: ${props => props.bgColor || BRAUN.accentSoft};
  margin-right: 12px;
  flex-shrink: 0;
  
  svg {
    color: ${props => props.iconColor || BRAUN.accent};
    font-size: 18px;
  }

  ${mobile({
    width: "30px",
    height: "30px",
    marginRight: "10px",
  })}
`;

const Label = styled.span`
  font-weight: 600;
  color: ${BRAUN.text};
  margin-right: 8px;
  font-size: 13px;

  ${mobile({
    fontSize: "12px",
  })}
`;

const Value = styled.span`
  color: ${BRAUN.textMuted};
  font-size: 13px;
  line-height: 1.45;
  word-break: break-word;

  ${mobile({
    fontSize: "12px",
  })}
`;

const Section = styled.div`
  width: 100%;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
  
  ${mobile({
  gridTemplateColumns: "1fr",
})}
`;

const ReceiptButton = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${BRAUN.accent};
    border-radius: 3px;
  }
`;

const StyledTable = styled(Table)`
  width: 100%;
  min-width: 100%;
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
  
  ${mobile({
  minWidth: "100%",
})}
`;

const TableHeader = styled.th`
  color: #ffffff;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 12px 16px;
  border-bottom: none;
  
  ${mobile({
  fontSize: "0.75rem",
  padding: "8px 10px",
  fontWeight: "500",
})}
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 0.875rem;
  
  ${mobile({
  fontSize: "0.75rem",
  padding: "8px 10px",
  fontWeight: "400",
})}
`;

const OrderItemsSection = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const OrderItemsCard = styled(Card)`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: none;
  border: 1px solid ${BRAUN.border};
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  
  ${mobile({
  borderRadius: "8px",
  maxWidth: "100%",
  border: `1px solid ${BRAUN.border}`,
})}
`;

const OrderDetail = () => {
  const { orderDetail } = useSelector((state) => state.userOrders);

  /* This order's money, worked out in ONE place — see utils/orderSummary.js
     for what was wrong before and which field is authoritative. It lives in a
     pure module rather than inline so the arithmetic can be tested, which
     matters more than usual for a screen that already shipped a sum a customer
     could see was broken. */
  const summary = useMemo(() => buildOrderSummary(orderDetail), [orderDetail]);
  const user = useSelector((state) => state.user.currentUser);
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  // Image preview modal state
  const [previewImage, setPreviewImage] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [freightResponding, setFreightResponding] = useState(false);

  const respondToFreight = async (decision) => {
    if (decision === "REJECT" && !window.confirm("Decline this freight quote? The merchandise order will remain on hold.")) return;
    setFreightResponding(true);
    try {
      await userRequest.post(`/weborders/${id}/freight/respond`, { decision });
      toast.success(decision === "ACCEPT" ? "Freight quote accepted." : "Freight quote declined.");
      dispatch(fetchOrderDetails(id));
    } catch (error) {
      toast.error(error.response?.data?.error?.message || "Could not update the freight quote.");
    } finally {
      setFreightResponding(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setPreviewImage(imageUrl);
    setShowImagePreview(true);
  };

  useEffect(() => {
    dispatch(fetchOrderDetails(id));
  }, [dispatch, id]);

  // ── Live status over Socket.IO ────────────────────────────────────────────
  // This page previously had NO realtime transport at all — a customer had to
  // refresh to see PROCESSING → DISPATCHED → DELIVERED move. Same pattern as
  // MyProfile / CODOrderScreen: join `orderRoom:${client_id}`; the backend
  // Mongo change stream emits `orderUpdate` (full order doc) on every order
  // write; we act only on updates for THIS order. `updateOrder` patches both
  // the list and `orderDetail` in the slice for an instant chip/stepper move,
  // then a refetch reconciles populated refs (shippingCost_id etc.) that the
  // raw change-stream doc doesn't carry.
  const clientId = user?.result?._id || user?._id;
  useEffect(() => {
    if (!clientId || !id) return;

    // Carries the customer's token: their rooms need it (utils/userSocket.js).
    const socket = createUserSocket();

    socket.on("connect", () => {
      socket.emit("join", `orderRoom:${clientId}`);
    });

    socket.on("orderUpdate", (updatedOrder) => {
      if (String(updatedOrder?._id) !== String(id)) return;
      console.log(
        "[OrderDetail] orderUpdate:",
        updatedOrder?._id,
        updatedOrder?.delivery_status
      );
      dispatch(updateOrder(updatedOrder));
      dispatch(fetchOrderDetails(id));
    });

    return () => {
      socket.off("orderUpdate");
      socket.disconnect();
    };
  }, [clientId, id, dispatch]);

  const handleGenerateReceipt = () => {
    // Build query parameters with proper URL encoding
    const params = new URLSearchParams({
      customerName: orderDetail.customerName || '',
      customerNumber: orderDetail.shippingAddress?.customerNumber || '',
      shippingAddress: orderDetail.shippingAddress?.address ||
        orderDetail.shippingAddress?.address?.address ||
        orderDetail.shippingAddress?.locationName?.name || '',
      // Same authoritative fee the screen shows. This line used to read the
      // deprecated legacy pair, so the printed receipt — the thing a customer
      // keeps and disputes against — also failed to add up.
      shippingCost: summary.deliveryFee,
      orderNumber: orderDetail._id || '',
      totalAmount: orderDetail.totalPrice || 0,
      taxAmount: orderDetail.taxPrice || 0,
      itemsInCart: summary.unitCount,
      orderedItems: JSON.stringify(orderDetail.orderItems || []),
      Qty: orderDetail.orderItems?.map(item => item.qty).join(',') || '',
      itemPx: orderDetail.orderItems?.map(item => item.price).join(',') || '',
      cartPx: orderDetail.itemsPrice || 0,
      updatedAt: moment(orderDetail.updatedAt).utcOffset("+03:00").format("lll"),
      paymentStatus: orderDetail.isPaid ? "Paid" : "Not Paid",
      paidDate: orderDetail.paidAt ? moment(orderDetail.paidAt).utcOffset("+03:00").format("lll") : 'N/A'
    });

    const url = `${process.env.REACT_APP_SERVER_URL}/weborders/get_receipt?${params.toString()}`;

    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.responseType = "arraybuffer";

    xhr.onload = function (e) {
      if (this.status === 200) {
        const blob = new Blob([xhr.response], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Lusuku_Receipt_${orderDetail._id}.pdf`;
        link.click();
        // Clean up the object URL
        setTimeout(() => window.URL.revokeObjectURL(link.href), 100);
      } else {
        console.error("Failed to generate receipt. Status:", this.status);
        alert("Failed to generate receipt. Please try again.");
      }
    };

    xhr.onerror = function () {
      console.error("Network error while generating receipt");
      alert("Network error. Please check your connection and try again.");
    };

    xhr.send();
  };

  const deliveryStatus = orderDetail?.delivery_status || "PROCESSING";
  // "Scheduled delivery" and the server's window label, or null for Express.
  const scheduledDelivery = describeOrderDeliveryTime(orderDetail);
  const mobileStatusColor =
    deliveryStatus === "PROCESSING" ? "#e3b200" :
    deliveryStatus === "ASSIGNED" ? "#005cad" :
    deliveryStatus === "PICKED_UP" ? "#7b1fa2" :
    deliveryStatus === "DISPATCHED" ? "#ad0062" :
    deliveryStatus === "DELIVERED" ? "#195400" :
    deliveryStatus === "REJECTED" ? "#ad0000" : "#04020f";

  return (
    <>
      <Navbar />
      <StyledContainer>
        <Wrapper>
              <PageContent>
                <Section>
                  <StyledPaper elevation={6}>
                    <Section>
                      {/* Back Button */}
                      <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={() => history.goBack()}
                        style={{
                          marginBottom: "16px",
                          borderColor: BRAUN.border,
                          color: BRAUN.text,
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          textTransform: "none",
                          borderRadius: "6px",
                          padding: "8px 14px",
                          backgroundColor: BRAUN.surface,
                        }}
                      >
                        Back to Orders
                      </Button>
                      {/* Header Section */}
                      <HeaderSection>
                        <div>
                          <Typography
                            variant="h6"
                            style={{
                              color: "#ffffff",
                              fontWeight: 700,
                              marginBottom: "6px",
                              fontSize: "1.1rem"
                            }}
                          >
                            {typeof orderDetail?.client_id === 'object' ? orderDetail?.client_id?.name : (orderDetail?.customerName || '')}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{
                              color: "rgba(255,255,255,0.82)",
                              fontSize: "0.82rem"
                            }}
                          >
                            Order #{orderDetail?.order_number || orderDetail?._id}
                          </Typography>
                        </div>

                        {/* Status Summary */}
                        <StatusSummary>
                          <StatusBadge>
                            <StatusLabel>{orderDetail?.isPackageDelivery ? "Type" : "Items"}</StatusLabel>
                            <StatusValue>
                              {orderDetail?.isPackageDelivery
                                ? <LocalShipping style={{ fontSize: 20 }} />
                                : summary.unitCount
                              }
                            </StatusValue>
                          </StatusBadge>

                          <StatusBadge>
                            <StatusLabel>Payment</StatusLabel>
                            <StatusValue>
                              {orderDetail?.isPaid ? (
                                <span style={{ color: BRAUN.success }}>✓</span>
                              ) : (
                                <span style={{ color: BRAUN.danger }}>✗</span>
                              )}
                            </StatusValue>
                          </StatusBadge>

                          <StatusBadge>
                            <StatusLabel>Status</StatusLabel>
                            <StatusValue style={{ fontSize: "0.75rem", textAlign: "center" }}>
                              {orderDetail?.delivery_status || "N/A"}
                            </StatusValue>
                          </StatusBadge>

                          <StatusBadge>
                            <StatusLabel>Total</StatusLabel>
                            <StatusValue style={{ fontSize: "0.85rem" }}>
                              {orderDetail?.totalPrice?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0"}
                            </StatusValue>
                          </StatusBadge>
                        </StatusSummary>
                      </HeaderSection>

                      {orderDetail?._id && (
                        <DeliveryAdjustments
                          orderId={orderDetail._id}
                          refreshKey={`${orderDetail.deliveryFee}-${orderDetail.deliveryAdjustmentsTotal}`}
                          onChanged={() => dispatch(fetchOrderDetails(id))}
                        />
                      )}

                      {orderDetail?.freight?.required && (
                        <div style={{ background: "#fff8e1", border: "1px solid #f2c94c", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                          <Typography variant="h6" style={{ fontWeight: 800, color: "#5d4300", fontSize: "1rem" }}>
                            Arranged freight · {String(orderDetail.freight.status || "").replaceAll("_", " ")}
                          </Typography>
                          <Typography variant="body2" style={{ color: "#6b5300", marginTop: 5 }}>
                            Delivery was not included in the merchandise total. Any freight charge requires your approval before dispatch.
                          </Typography>
                          {orderDetail.freight.quotedAt && (
                            <div style={{ marginTop: 10 }}>
                              <strong>Freight quote:</strong>{" "}
                              {formatCurrency(orderDetail.freight.quotedAmount)} · {String(orderDetail.freight.paymentTerms || "").replaceAll("_", " ").toLowerCase()}
                              {orderDetail.freight.quoteNotes ? <div style={{ marginTop: 4 }}>{orderDetail.freight.quoteNotes}</div> : null}
                            </div>
                          )}
                          {orderDetail.freight.status === "QUOTED" && (
                            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                              <Button variant="contained" color="primary" disabled={freightResponding} onClick={() => respondToFreight("ACCEPT")}>Accept freight quote</Button>
                              <Button variant="outlined" color="secondary" disabled={freightResponding} onClick={() => respondToFreight("REJECT")}>Decline</Button>
                            </div>
                          )}
                          {orderDetail.freight.appointmentStart && (
                            <div style={{ marginTop: 10 }}>
                              <strong>Appointment:</strong> {moment(orderDetail.freight.appointmentStart).format("lll")}
                              {orderDetail.freight.carrierName ? ` · ${orderDetail.freight.carrierName}` : ""}
                              {orderDetail.freight.trackingId ? ` · Tracking ${orderDetail.freight.trackingId}` : ""}
                            </div>
                          )}
                        </div>
                      )}

                      <DesktopStepperWrapper>
                        <OrderProgressStepper
                          currentStatus={orderDetail?.delivery_status}
                          isPaid={orderDetail?.isPaid}
                          isPackageDelivery={orderDetail?.isPackageDelivery}
                        />
                      </DesktopStepperWrapper>

                      <MobileProgressCard>
                        <MobileProgressHeader>
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 700,
                              color: "#ffffff",
                              margin: 0,
                            }}
                          >
                            Order Progress
                          </Typography>
                          <Chip
                            label={deliveryStatus}
                            size="small"
                            style={{
                              backgroundColor: mobileStatusColor,
                              color: BRAUN.surface,
                              fontWeight: 700,
                            }}
                          />
                        </MobileProgressHeader>

                        <MobileProgressMeta>
                          <MobileMetaItem>
                            <MobileMetaLabel>Status</MobileMetaLabel>
                            {/* Never the raw enum — a ticket order rendered "NOT_APPLICABLE",
                  which is a correct database value but not a sentence. */}
              <MobileMetaValue>{deliveryLabel(orderDetail)}</MobileMetaValue>
                          </MobileMetaItem>
                          <MobileMetaItem>
                            <MobileMetaLabel>Payment</MobileMetaLabel>
                            <MobileMetaValue>{orderDetail?.isPaid ? "Paid" : "Pending"}</MobileMetaValue>
                          </MobileMetaItem>
                          <MobileMetaItem>
                            <MobileMetaLabel>{orderDetail?.isPackageDelivery ? "Type" : "Items"}</MobileMetaLabel>
                            <MobileMetaValue>
                              {orderDetail?.isPackageDelivery ? "Package Delivery" : summary.itemsLabel}
                            </MobileMetaValue>
                          </MobileMetaItem>
                        </MobileProgressMeta>
                      </MobileProgressCard>

                      {/* Two Column Layout for Cards */}
                      <TwoColumnLayout>
                        {/* Order Summary Card */}
                        <InfoCard borderColor={orderDetail?.isPackageDelivery ? "#9C27B0" : "#017109"}>
                          <InfoRow>
                            <IconWrapper bgColor={orderDetail?.isPackageDelivery ? "#f3e5f5" : "#e8f5e9"} iconColor={orderDetail?.isPackageDelivery ? "#9C27B0" : "#017109"}>
                              {orderDetail?.isPackageDelivery ? <LocalShipping /> : <ShoppingCart />}
                            </IconWrapper>
                            <div style={{ flex: 1 }}>
                              <Label>{orderDetail?.isPackageDelivery ? "Package Price:" : "Cart Total:"}</Label>
                              <Value style={{ fontSize: "18px", fontWeight: 600, color: orderDetail?.isPackageDelivery ? "#9C27B0" : "#017109" }}>
                                {formatCurrency(orderDetail?.isPackageDelivery ? summary.totalPrice : summary.itemsPrice)}
                              </Value>
                            </div>
                            <Chip
                              label={orderDetail?.isPackageDelivery ? "Package Delivery" : summary.itemsLabel}
                              size="small"
                              style={{
                                backgroundColor: orderDetail?.isPackageDelivery ? "#9C27B0" : "#017109",
                                color: "#ffffff",
                                fontWeight: 600
                              }}
                            />
                          </InfoRow>

                          {/* The arithmetic, in one column, under the figure it
                              explains. This card was mostly empty space and the
                              parts of the sum were scattered over three cards,
                              which is why a missing 11,000 went unnoticed. Every
                              row reads from `summary`, so the lines shown here
                              always add to the Total printed below them. */}
                          {!orderDetail?.isPackageDelivery && (
                            <div
                              style={{
                                marginTop: "14px",
                                paddingTop: "12px",
                                borderTop: "1px solid #eceff0",
                              }}
                            >
                              {[
                                { key: "items", label: summary.itemsLabel, value: summary.itemsPrice },
                                { key: "delivery", label: "Delivery", value: summary.deliveryFee },
                                ...(summary.processingFee
                                  ? [{
                                      key: "processing",
                                      label: summary.processingLabel,
                                      value: summary.processingFee,
                                    }]
                                  : []),
                                /* VAT is INCLUSIVE, so it is shown and not added.
                                   Summing it here is what made every taxed order
                                   print a "Discount" equal to the tax. */
                                ...(summary.taxPrice
                                  ? [{ key: "tax", label: "VAT (included above)", value: summary.taxPrice }]
                                  : []),
                                /* Only ever appears when the parts genuinely do
                                   not explain the total. Naming it beats hiding
                                   it: a customer disputing a charge is owed the
                                   arithmetic, and we are owed the alarm. */
                                ...(Math.abs(summary.adjustment) >= 1
                                  ? [{
                                      key: "adjustment",
                                      label: summary.adjustment < 0 ? "Discount" : "Other charges",
                                      value: summary.adjustment,
                                    }]
                                  : []),
                              ].map((row) => (
                                <div
                                  key={row.key}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "baseline",
                                    padding: "5px 0",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  <span style={{ color: "#7f8c8d" }}>{row.label}</span>
                                  <span style={{ color: "#2c3e50", fontWeight: 600 }}>
                                    {formatCurrency(row.value)}
                                  </span>
                                </div>
                              ))}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "baseline",
                                  padding: "9px 0 0",
                                  marginTop: "5px",
                                  borderTop: "1px solid #eceff0",
                                }}
                              >
                                <span style={{ color: "#2c3e50", fontWeight: 700, fontSize: "0.9rem" }}>
                                  Total
                                </span>
                                <span style={{ color: "#017109", fontWeight: 700, fontSize: "1.05rem" }}>
                                  {formatCurrency(summary.totalPrice)}
                                </span>
                              </div>
                            </div>
                          )}
                        </InfoCard>

                        {/* Payment & Agent Info */}
                        <InfoCard borderColor="#2196F3">
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 700,
                              marginBottom: "10px",
                              color: "#2c3e50",
                              fontSize: "0.875rem"
                            }}
                          >
                            Payment Information
                          </Typography>
                          <InfoRow>
                            <IconWrapper bgColor="#e3f2fd" iconColor="#2196F3">
                              <Payment />
                            </IconWrapper>
                            <div>
                              <Label>Payment Method:</Label>
                              <Value>{orderDetail?.paymentMethod}</Value>
                            </div>
                          </InfoRow>
                          <InfoRow>
                            <IconWrapper bgColor="#e3f2fd" iconColor="#2196F3">
                              <Receipt />
                            </IconWrapper>
                            <div>
                              <Label>Total Cost:</Label>
                              <Value style={{ fontWeight: 600, color: "#e91e63" }}>
                                {formatCurrency(summary.totalPrice)}
                              </Value>
                            </div>
                          </InfoRow>
                          {orderDetail?.agent_id && (
                            <InfoRow>
                              <IconWrapper bgColor="#e3f2fd" iconColor="#2196F3">
                                <Phone />
                              </IconWrapper>
                              <div>
                                <Label>Agent:</Label>
                                <Value>{orderDetail?.agent_id?.name || orderDetail?.agent_id}</Value>
                              </div>
                            </InfoRow>
                          )}
                          {orderDetail?.shippingAddress?.customerNumber && (
                            <InfoRow>
                              <IconWrapper bgColor="#e3f2fd" iconColor="#2196F3">
                                <Phone />
                              </IconWrapper>
                              <div>
                                <Label>Customer Number:</Label>
                                <Value>{orderDetail?.shippingAddress?.customerNumber}</Value>
                              </div>
                            </InfoRow>
                          )}
                        </InfoCard>
                      </TwoColumnLayout>

                      <TwoColumnLayout>
                        {/* Delivery Information */}
                        <InfoCard borderColor="#FF9800">
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 700,
                              marginBottom: "10px",
                              color: "#2c3e50",
                              fontSize: "0.875rem"
                            }}
                          >
                            Delivery Details
                          </Typography>
                          {orderDetail?.isPackageDelivery ? (
                            <>
                              <InfoRow>
                                <IconWrapper bgColor="#e8f5e9" iconColor="#4CAF50">
                                  <MyLocation />
                                </IconWrapper>
                                <div>
                                  <Label>Pickup:</Label>
                                  <Value>{orderDetail?.pickupAddress?.address || "N/A"}</Value>
                                </div>
                              </InfoRow>
                              <InfoRow>
                                <IconWrapper bgColor="#ffebee" iconColor="#f44336">
                                  <FlagOutlined />
                                </IconWrapper>
                                <div>
                                  <Label>Delivery:</Label>
                                  <Value>{orderDetail?.shippingAddress?.address || "N/A"}</Value>
                                  {(() => {
                                    const pin = describeDropoffPrecision(orderDetail?.shippingAddress);
                                    if (!pin) return null;
                                    const t = DROPOFF_TONES[pin.tone];
                                    const label =
                                      pin.tone === "places"
                                        ? "Pinned by address search"
                                        : pin.metres == null
                                        ? "Pinned by GPS"
                                        : `Pinned by GPS · accurate to ±${pin.metres} m`;
                                    return (
                                      <span
                                        title={pin.detail}
                                        style={{
                                          display: "inline-flex", alignItems: "center", gap: 6, marginTop: 4,
                                          padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                                          background: t.bg, border: `1px solid ${t.border}`, color: t.text,
                                        }}
                                      >
                                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.dot }} />
                                        {label}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </InfoRow>
                              {scheduledDelivery && (
                                <InfoRow>
                                  <IconWrapper bgColor="#fff3e0" iconColor="#FF9800">
                                    <CalendarToday />
                                  </IconWrapper>
                                  <div>
                                    <Label>{scheduledDelivery.title}:</Label>
                                    <Value style={{ color: "#9FC131", fontWeight: 600 }}>
                                      {scheduledDelivery.detail}
                                    </Value>
                                  </div>
                                </InfoRow>
                              )}
                            </>
                          ) : (
                            <>
                              <InfoRow>
                                <IconWrapper bgColor="#fff3e0" iconColor="#FF9800">
                                  <LocalShipping />
                                </IconWrapper>
                                <div>
                                  <Label>Delivery Zone:</Label>
                                  <Value>
                                    {orderDetail?.shippingAddress?.selectedVillage?.village ||
                                      orderDetail?.shippingAddress?.locationName?.vicinity ||
                                      orderDetail?.shippingAddress?.description}
                                  </Value>
                                </div>
                              </InfoRow>
                              <InfoRow>
                                <IconWrapper bgColor="#fff3e0" iconColor="#FF9800">
                                  <LocationOn />
                                </IconWrapper>
                                <div>
                                  <Label>Address:</Label>
                                  <Value>
                                    {orderDetail?.shippingAddress?.address ||
                                      orderDetail?.shippingAddress?.address?.address ||
                                      orderDetail?.shippingAddress?.locationName?.name}
                                  </Value>
                                </div>
                              </InfoRow>
                              <InfoRow>
                                <IconWrapper bgColor="#fff3e0" iconColor="#FF9800">
                                  <Receipt />
                                </IconWrapper>
                                <div>
                                  <Label>Shipping Fees:</Label>
                                  <Value style={{ fontWeight: 600 }}>
                                    {orderDetail?.freight?.required ? "Quoted separately" : formatCurrency(summary.deliveryFee)}
                                  </Value>
                                </div>
                              </InfoRow>
                              <InfoRow>
                                <IconWrapper bgColor="#fff3e0" iconColor="#FF9800">
                                  <CalendarToday />
                                </IconWrapper>
                                <div>
                                  {scheduledDelivery ? (
                                    <>
                                      <Label>{scheduledDelivery.title}:</Label>
                                      <Value style={{ color: "#9FC131", fontWeight: 600 }}>
                                        {scheduledDelivery.detail}
                                      </Value>
                                    </>
                                  ) : (
                                    <>
                                      <Label>Delivery Schedule:</Label>
                                      <Value style={{ color: "#9FC131", fontWeight: 600 }}>
                                        {orderDetail?.deliverySchedule || "Call in"}
                                      </Value>
                                    </>
                                  )}
                                </div>
                              </InfoRow>
                            </>
                          )}
                        </InfoCard>
                        {/* Order Status & Timeline */}
                        <InfoCard borderColor="#9C27B0">
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 700,
                              marginBottom: "10px",
                              color: "#2c3e50",
                              fontSize: "0.875rem"
                            }}
                          >
                            Order Status & Timeline
                          </Typography>
                          <InfoRow>
                            <IconWrapper bgColor="#f3e5f5" iconColor="#9C27B0">
                              <CalendarToday />
                            </IconWrapper>
                            <div>
                              <Label>Order Placed:</Label>
                              <Value style={{ color: "#017109" }}>
                                {moment(orderDetail?.createdAt).format("MMMM Do YYYY, h:mm a")}
                              </Value>
                            </div>
                          </InfoRow>
                          <InfoRow>
                            <IconWrapper
                              bgColor={
                                orderDetail?.delivery_status === "PROCESSING" ? "#fff8e1" :
                                  orderDetail?.delivery_status === "ASSIGNED" ? "#e3f2fd" :
                                    orderDetail?.delivery_status === "DISPATCHED" ? "#fce4ec" :
                                      orderDetail?.delivery_status === "DELIVERED" ? "#e8f5e9" :
                                        orderDetail?.delivery_status === "REJECTED" ? "#ffebee" : "#f5f5f5"
                              }
                              iconColor={
                                orderDetail?.delivery_status === "PROCESSING" ? "#e3b200" :
                                  orderDetail?.delivery_status === "ASSIGNED" ? "#005cad" :
                                    orderDetail?.delivery_status === "DISPATCHED" ? "#ad0062" :
                                      orderDetail?.delivery_status === "DELIVERED" ? "#195400" :
                                        orderDetail?.delivery_status === "REJECTED" ? "#ad0000" : "#04020f"
                              }
                            >
                              <CalendarToday />
                            </IconWrapper>
                            <div >
                              <Label>Status:</Label>
                              <Chip
                                label={orderDetail?.delivery_status}
                                size="small"
                                style={{
                                  backgroundColor:
                                    orderDetail?.delivery_status === "PROCESSING" ? "#e3b200" :
                                      orderDetail?.delivery_status === "ASSIGNED" ? "#005cad" :
                                        orderDetail?.delivery_status === "DISPATCHED" ? "#ad0062" :
                                          orderDetail?.delivery_status === "DELIVERED" ? "#195400" :
                                            orderDetail?.delivery_status === "REJECTED" ? "#ad0000" : "#04020f",
                                  color: "#ffffff",
                                  fontWeight: 600,
                                  marginLeft: "8px"
                                }}
                              />
                              {orderDetail?.deliveredAt && (
                                <Value style={{ display: "block", marginTop: "4px" }}>
                                  Delivered {moment(orderDetail?.deliveredAt).fromNow()}
                                </Value>
                              )}
                            </div>
                          </InfoRow>
                          {orderDetail?.riderId && (
                            <InfoRow>
                              <IconWrapper bgColor="#f3e5f5" iconColor="#9C27B0">
                                <LocalShipping />
                              </IconWrapper>
                              <div>
                                <Label>Delivery Agent:</Label>
                                <Value>
                                  {orderDetail?.riderId?.firstName + " " + orderDetail?.riderId?.lastName}
                                </Value>
                              </div>
                            </InfoRow>
                          )}
                        </InfoCard>
                      </TwoColumnLayout>

                      {/* Payment Status */}
                      <InfoCard
                        borderColor={orderDetail?.isPaid ? "#4CAF50" : "#f44336"}
                        style={{
                          backgroundColor: orderDetail?.isPaid ? "#f1f8f4" : "#fef5f5"
                        }}
                      >
                        <InfoRow>
                          <div style={{ flex: 1 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={orderDetail?.isPaid ?? false}
                                  color="primary"
                                  disabled={true}
                                />
                              }
                              label={
                                <span style={{
                                  fontWeight: 600,
                                  color: orderDetail?.isPaid ? "#4CAF50" : "#f44336"
                                }}>
                                  {orderDetail?.isPaid ? "Payment Completed" : "Payment Pending"}
                                </span>
                              }
                            />
                            {orderDetail?.isPaid && (
                              <Typography
                                variant="caption"
                                display="block"
                                style={{
                                  color: "#4CAF50",
                                  marginLeft: "32px",
                                  fontSize: "12px"
                                }}
                              >
                                Paid on {moment(orderDetail?.paidAt).utcOffset("+03:00").format("lll")}
                              </Typography>
                            )}
                          </div>
                        </InfoRow>
                      </InfoCard>
                    </Section>
                    {(orderDetail?.country || "UG") === "UG" && <MerchantPaymentReport key={orderDetail._id} orderId={orderDetail._id} />}
                    <ReceiptButton>
                      <Button
                        variant="contained"
                        onClick={handleGenerateReceipt}
                        disabled={!orderDetail?.isPaid}
                        startIcon={<Receipt />}
                        style={{
                          backgroundColor: orderDetail?.isPaid ? BRAUN.accent : BRAUN.border,
                          color: orderDetail?.isPaid ? BRAUN.surface : BRAUN.textMuted,
                          padding: "10px 20px",
                          borderRadius: "6px",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          textTransform: "none",
                          boxShadow: "none",
                          transition: "all 0.2s ease"
                        }}
                        onMouseEnter={(e) => {
                          if (orderDetail?.isPaid) {
                            e.currentTarget.style.backgroundColor = "#111111";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (orderDetail?.isPaid) {
                            e.currentTarget.style.backgroundColor = BRAUN.accent;
                          }
                        }}
                      >
                        {orderDetail?.isPaid ? "Generate Receipt" : "Payment Required"}
                      </Button>
                    </ReceiptButton>

                    {/* Report an issue on THIS order, beside the receipt action.
                        The whole flow (form, validation, api call, navigation)
                        lives in the component, so nothing on this screen
                        changes shape. See components/SupportCases/. */}
                    <ReportIssueButton order={orderDetail} />

                    {/* Package Delivery Details - Only shown for package orders */}
                    {orderDetail?.isPackageDelivery && (
                      <OrderItemsSection>
                        <Typography
                          variant="h6"
                          style={{
                            fontWeight: 700,
                            marginBottom: "12px",
                            color: "#2c3e50",
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "1.1rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          <LocalShipping style={{ color: "#9C27B0" }} />
                          Package Delivery Details
                        </Typography>

                        <TwoColumnLayout>
                          {/* Package Info */}
                          <InfoCard borderColor="#9C27B0">
                            <Typography
                              variant="subtitle2"
                              style={{
                                fontWeight: 700,
                                marginBottom: "10px",
                                color: "#2c3e50",
                                fontSize: "0.875rem"
                              }}
                            >
                              Package Information
                            </Typography>
                            <InfoRow>
                              <IconWrapper bgColor="#f3e5f5" iconColor="#9C27B0">
                                <Description />
                              </IconWrapper>
                              <div>
                                <Label>Description:</Label>
                                <Value>{orderDetail?.package?.description || "N/A"}</Value>
                              </div>
                            </InfoRow>
                            <InfoRow>
                              <IconWrapper bgColor="#f3e5f5" iconColor="#9C27B0">
                                <FitnessCenter />
                              </IconWrapper>
                              <div>
                                <Label>Weight:</Label>
                                <Value>{orderDetail?.package?.weight} kg</Value>
                              </div>
                            </InfoRow>
                            {orderDetail?.package?.isFragile && (
                              <InfoRow>
                                <IconWrapper bgColor="#fff3e0" iconColor="#FF9800">
                                  <WarningRounded />
                                </IconWrapper>
                                <div>
                                  <Label>Handling:</Label>
                                  <Chip
                                    label="Fragile"
                                    size="small"
                                    style={{
                                      backgroundColor: "#fff3e0",
                                      color: "#FF9800",
                                      fontWeight: 600
                                    }}
                                  />
                                </div>
                              </InfoRow>
                            )}
                            {orderDetail?.package?.specialInstructions && (
                              <InfoRow>
                                <IconWrapper bgColor="#f3e5f5" iconColor="#9C27B0">
                                  <NoteOutlined />
                                </IconWrapper>
                                <div>
                                  <Label>Special Instructions:</Label>
                                  <Value>{orderDetail?.package?.specialInstructions}</Value>
                                </div>
                              </InfoRow>
                            )}
                          </InfoCard>

                          {/* Route Info */}
                          <InfoCard borderColor="#4CAF50">
                            <Typography
                              variant="subtitle2"
                              style={{
                                fontWeight: 700,
                                marginBottom: "10px",
                                color: "#2c3e50",
                                fontSize: "0.875rem"
                              }}
                            >
                              Pickup & Delivery Route
                            </Typography>
                            <InfoRow>
                              <IconWrapper bgColor="#e8f5e9" iconColor="#4CAF50">
                                <MyLocation />
                              </IconWrapper>
                              <div>
                                <Label>Pickup Location:</Label>
                                <Value>{orderDetail?.pickupAddress?.address || "N/A"}</Value>
                              </div>
                            </InfoRow>
                            {orderDetail?.pickupAddress?.senderPhone && (
                              <InfoRow>
                                <IconWrapper bgColor="#e8f5e9" iconColor="#4CAF50">
                                  <Phone />
                                </IconWrapper>
                                <div>
                                  <Label>Sender Phone:</Label>
                                  <Value>{orderDetail?.pickupAddress?.senderPhone}</Value>
                                </div>
                              </InfoRow>
                            )}
                            <InfoRow>
                              <IconWrapper bgColor="#ffebee" iconColor="#f44336">
                                <FlagOutlined />
                              </IconWrapper>
                              <div>
                                <Label>Delivery Location:</Label>
                                <Value>{orderDetail?.shippingAddress?.address || "N/A"}</Value>
                              </div>
                            </InfoRow>
                            {(orderDetail?.shippingAddress?.recipientPhone || orderDetail?.shippingAddress?.customerNumber) && (
                              <InfoRow>
                                <IconWrapper bgColor="#ffebee" iconColor="#f44336">
                                  <Phone />
                                </IconWrapper>
                                <div>
                                  <Label>Recipient Phone:</Label>
                                  <Value>{orderDetail?.shippingAddress?.recipientPhone || orderDetail?.shippingAddress?.customerNumber}</Value>
                                </div>
                              </InfoRow>
                            )}
                          </InfoCard>
                        </TwoColumnLayout>
                      </OrderItemsSection>
                    )}

                    {/* Vendor / Brand Details — shown for brand orders */}
                    {(orderDetail?.isBrandOrder || orderDetail?.orderItems?.[0]?.restaurantData) && (() => {
                      const firstItem = orderDetail?.orderItems?.[0];
                      const rd = firstItem?.restaurantData ?? null;
                      const vendorObj = typeof firstItem?.vendorId === "object" ? firstItem.vendorId : null;
                      // The outlet (branch) a brand order is fulfilled from,
                      // when the order names one: its address is where the
                      // goods leave from, so it stands in for the brand's.
                      const outlet = orderDetail?.fulfilmentOutlet?.name
                        ? orderDetail.fulfilmentOutlet
                        : null;
                      const vendor = {
                        name: rd?.name || vendorObj?.name,
                        address: outlet?.address || rd?.address || vendorObj?.address,
                        phone: vendorObj?.phone || rd?.phone,
                        email: vendorObj?.email || rd?.email,
                        deliveryZoneRadius: vendorObj?.deliveryZoneRadius || rd?.deliveryZoneRadius,
                      };
                      if (!vendor.name && !outlet) return null;
                      return (
                        <InfoCard borderColor="#013A63" style={{ marginTop: 12 }}>
                          <Typography
                            variant="subtitle2"
                            style={{ fontWeight: 700, marginBottom: 10, color: "#013A63", fontSize: "0.875rem", display: "flex", alignItems: "center", gap: 6 }}
                          >
                            Vendor / Brand Details
                          </Typography>
                          <TwoColumnLayout style={{ marginBottom: 0 }}>
                            <div>
                              {vendor.name && (
                                <InfoRow>
                                  <IconWrapper bgColor="#e8f0fe" iconColor="#013A63">
                                    <ShoppingCart />
                                  </IconWrapper>
                                  <div>
                                    <Label>Brand:</Label>
                                    <Value style={{ fontWeight: 600, color: "#013A63" }}>{vendor.name}</Value>
                                  </div>
                                </InfoRow>
                              )}
                              {outlet && (
                                <InfoRow>
                                  <IconWrapper bgColor="#e8f0fe" iconColor="#013A63">
                                    <Storefront />
                                  </IconWrapper>
                                  <div>
                                    <Label>Fulfilled from:</Label>
                                    <Value style={{ fontWeight: 600, color: "#013A63" }}>{outlet.name}</Value>
                                  </div>
                                </InfoRow>
                              )}
                              {vendor.address && (
                                <InfoRow>
                                  <IconWrapper bgColor="#e8f0fe" iconColor="#013A63">
                                    <LocationOn />
                                  </IconWrapper>
                                  <div>
                                    <Label>Address:</Label>
                                    <Value>{vendor.address}</Value>
                                  </div>
                                </InfoRow>
                              )}
                              {vendor.phone && (
                                <InfoRow>
                                  <IconWrapper bgColor="#e8f0fe" iconColor="#013A63">
                                    <Phone />
                                  </IconWrapper>
                                  <div>
                                    <Label>Contact:</Label>
                                    <Value>{vendor.phone}</Value>
                                  </div>
                                </InfoRow>
                              )}
                            </div>
                            <div>
                              {orderDetail?.selectedEditionDate && (
                                <InfoRow>
                                  <IconWrapper bgColor="#e8f0fe" iconColor="#013A63">
                                    <CalendarToday />
                                  </IconWrapper>
                                  <div>
                                    <Label>Edition Date:</Label>
                                    <Value style={{ color: "#017109", fontWeight: 600 }}>
                                      {moment(orderDetail.selectedEditionDate).format("MMMM Do YYYY")}
                                    </Value>
                                  </div>
                                </InfoRow>
                              )}
                              {orderDetail?.brandFlowType && (
                                <InfoRow>
                                  <IconWrapper bgColor="#e8f0fe" iconColor="#013A63">
                                    <Receipt />
                                  </IconWrapper>
                                  <div>
                                    <Label>Order Type:</Label>
                                    <Chip
                                      label={orderDetail.brandFlowType}
                                      size="small"
                                      style={{ backgroundColor: "#013A63", color: "#fff", fontWeight: 600, fontSize: "0.7rem" }}
                                    />
                                  </div>
                                </InfoRow>
                              )}
                              {vendor.deliveryZoneRadius && (
                                <InfoRow>
                                  <IconWrapper bgColor="#e8f0fe" iconColor="#013A63">
                                    <LocalShipping />
                                  </IconWrapper>
                                  <div>
                                    <Label>Delivery Radius:</Label>
                                    <Value>{vendor.deliveryZoneRadius} km</Value>
                                  </div>
                                </InfoRow>
                              )}
                            </div>
                          </TwoColumnLayout>
                        </InfoCard>
                      );
                    })()}

                    {/* Order Items Section - Only shown for non-package orders */}
                    {!orderDetail?.isPackageDelivery && orderDetail?.orderItems?.length > 0 && (
                      <OrderItemsSection>
                        <Typography
                          variant="h6"
                          style={{
                            fontWeight: 700,
                            marginBottom: "12px",
                            color: "#2c3e50",
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "1.1rem"
                          }}
                        >
                          Order Items
                        </Typography>
                        <OrderItemsCard>
                          <TableWrapper>
                            <StyledTable>
                              <thead>
                                <tr style={{ background: "#2f2f2f" }}>
                                  <TableHeader className="text-center" style={{ width: "6%" }}>
                                    #
                                  </TableHeader>
                                  <TableHeader className="text-left" style={{ width: "30%" }}>
                                    Item Name
                                  </TableHeader>
                                  <TableHeader className="text-center" style={{ width: "12%" }}>
                                    Qty
                                  </TableHeader>
                                  <TableHeader className="text-center" style={{ width: "18%" }}>
                                    Unit Price
                                  </TableHeader>
                                  {/* Qty and unit price were both shown and their
                                      product was not, so the cart total above had
                                      to be taken on trust. */}
                                  <TableHeader className="text-center" style={{ width: "20%" }}>
                                    Subtotal
                                  </TableHeader>
                                  <TableHeader className="text-center" style={{ width: "14%" }}>
                                    Image
                                  </TableHeader>
                                </tr>
                              </thead>
                              <tbody>
                                {orderDetail?.orderItems?.map((item, index) => (
                                  <tr
                                    key={index}
                                    style={{
                                      borderBottom: index < orderDetail.orderItems.length - 1 ? `1px solid ${BRAUN.border}` : "none",
                                      backgroundColor: index % 2 === 0 ? "#f8f8f7" : BRAUN.surface,
                                      transition: "background-color 0.2s ease"
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#efefed"}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#f8f8f7" : BRAUN.surface}
                                  >
                                    <TableCell
                                      className="text-center"
                                      style={{
                                        color: BRAUN.text,
                                        fontWeight: 600
                                      }}
                                    >
                                      {index + 1}
                                    </TableCell>
                                    <TableCell
                                      className="text-left"
                                      style={{
                                        color: BRAUN.text,
                                        fontWeight: 500,
                                        textAlign: "left"
                                      }}
                                    >
                                      {item.name}
                                    </TableCell>
                                    <TableCell className="text-center">
                                      <Chip
                                        label={item.qty}
                                        size="small"
                                        style={{
                                          backgroundColor: BRAUN.accentSoft,
                                          color: BRAUN.text,
                                          fontWeight: 600,
                                          minWidth: "35px",
                                          height: "24px",
                                          fontSize: "0.75rem"
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell
                                      className="text-center"
                                      style={{
                                        color: BRAUN.text,
                                        fontWeight: 600
                                      }}
                                    >
                                      {formatCurrency(item?.price)}
                                    </TableCell>
                                    <TableCell
                                      className="text-center"
                                      style={{ color: BRAUN.text, fontWeight: 700 }}
                                    >
                                      {formatCurrency(
                                        (Number(item?.price) || 0) *
                                          (Number(item?.qty) > 0 ? Number(item.qty) : 1)
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center">
                                      <Image
                                        src={item.imageUrl || item.image}
                                        resizemode="contain"
                                        onClick={() => handleImageClick(item.imageUrl || item.image)}
                                        style={{
                                          width: 45,
                                          height: 45,
                                          borderRadius: "6px",
                                          objectFit: "cover",
                                          boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
                                          cursor: "pointer",
                                          transition: "transform 0.2s ease"
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                                      />
                                    </TableCell>
                                  </tr>
                                ))}
                              </tbody>
                            </StyledTable>
                          </TableWrapper>
                        </OrderItemsCard>
                      </OrderItemsSection>
                    )}
                  </StyledPaper>
                </Section>
              </PageContent>
        </Wrapper>
      </StyledContainer>

      {/* Image Preview Modal */}
      <Dialog
        open={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        maxWidth="md"
        PaperProps={{
          style: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "visible"
          }
        }}
      >
        <IconButton
          onClick={() => setShowImagePreview(false)}
          style={{
            position: "absolute",
            top: -10,
            right: -10,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            color: "#333",
            zIndex: 1
          }}
        >
          <Close />
        </IconButton>
        <DialogContent style={{ padding: 0 }}>
          {previewImage && (
            <img
              src={previewImage}
              alt="Product Preview"
              style={{
                width: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                borderRadius: "8px",
                backgroundColor: "#fff"
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrderDetail;
